import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API } from "@/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { PawPrint, Lock, Check, AlertCircle } from "lucide-react";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setError("Link non valido. Richiedi un nuovo reset password.");
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error("Le password non coincidono");
      return;
    }
    
    if (password.length < 6) {
      toast.error("La password deve avere almeno 6 caratteri");
      return;
    }
    
    setIsLoading(true);
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: password })
      });
      
      if (res.ok) {
        setSuccess(true);
        toast.success("Password reimpostata con successo!");
      } else {
        const data = await res.json();
        setError(data.detail || "Errore nel reset della password");
        toast.error(data.detail || "Errore nel reset della password");
      }
    } catch (error) {
      setError("Errore di connessione");
      toast.error("Errore di connessione");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-xl p-8">
          {/* Logo */}
          <div className="flex flex-col items-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#FF6B4A] to-[#FF8F6B] flex items-center justify-center mb-4 shadow-lg shadow-[#FF6B4A]/30">
              <PawPrint className="text-white" size={32} />
            </div>
            <h1 className="text-2xl font-bold text-[#1D1D1F]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {success ? "Password Reimpostata!" : "Reimposta Password"}
            </h1>
          </div>

          {error && !success && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3">
              <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="text-red-700 font-medium">Errore</p>
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            </div>
          )}

          {success ? (
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
                <Check className="text-emerald-600" size={40} />
              </div>
              <p className="text-[#6E6E73] mb-6">
                La tua password è stata reimpostata con successo. Ora puoi accedere con la nuova password.
              </p>
              <Button
                onClick={() => navigate("/")}
                className="w-full h-12 bg-[#FF6B4A] hover:bg-[#E05A3A] text-white rounded-xl font-semibold"
              >
                Vai al Login
              </Button>
            </div>
          ) : !error ? (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label className="text-[#1D1D1F] text-sm font-medium mb-2 block">
                  Nuova Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#86868B]" size={18} />
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Minimo 6 caratteri"
                    className="h-12 rounded-xl bg-[#F5F5F7] border-0 pl-10"
                    required
                    minLength={6}
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-[#1D1D1F] text-sm font-medium mb-2 block">
                  Conferma Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#86868B]" size={18} />
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Ripeti la password"
                    className="h-12 rounded-xl bg-[#F5F5F7] border-0 pl-10"
                    required
                    minLength={6}
                  />
                </div>
              </div>
              
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-12 bg-[#FF6B4A] hover:bg-[#E05A3A] text-white rounded-xl font-semibold"
              >
                {isLoading ? "Reimpostazione in corso..." : "Reimposta Password"}
              </Button>
            </form>
          ) : (
            <div className="text-center">
              <Button
                onClick={() => navigate("/")}
                variant="outline"
                className="w-full h-12 rounded-xl"
              >
                Torna alla Home
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
